import React, { useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Card, CardBody, Col, Container, Nav, NavItem, Row, TabContent, Table, TabPane, Tooltip, NavLink } from "reactstrap";
import classnames from "classnames";
import UserPlanSubscriptionList from "./UserPlanSubscriptionList";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { toast } from "react-toastify";
import DeleteModal from "../../Components/Common/DeleteModal";
import { deletePlanSubscription, viewPlanSubscription } from "./_requests";
import { Spinner } from "reactstrap";

const ViewPlanSubscription = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [details, setDetails] = useState<any>(location.state?.details || null);
  const [loading, setLoading] = useState<boolean>(!location.state?.details);

  const [showMore, setShowMore] = useState(false);
  const toggleReadMore = () => setShowMore(!showMore);
  const [activeTab, setActiveTab] = useState("1");
  const toggleTab = (tab: string) => setActiveTab(tab);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  React.useEffect(() => {
    if (!details && id) {
      setLoading(true);
      viewPlanSubscription(id)
        .then((res: any) => {
          if (res.status === true || res.data) {
            // Depending on interceptor, res might be the body or the data
            const fetchedDetails = res.data?.data || res.data || res;
            setDetails(fetchedDetails);
          } else {
            toast.error("Failed to fetch plan details");
            navigate("/plan-subscription");
          }
        })
        .catch((err) => {
          console.error("Error fetching plan details:", err);
          toast.error("An error occurred while fetching plan details");
          navigate("/plan-subscription");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, details, navigate]);

  const sanitizeHTML = useCallback((html: string) => {
    return html?.replace(/<script[^>]*>([\S\s]*?)<\/script>/gi, "");
  }, []);

  const sanitizedContent = sanitizeHTML(details?.description || "");

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  // If no details and not loading, redirect back
  if (!details && !loading) {
    return null; // The useEffect will handle navigation
  }

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deletePlanSubscription(id);
      toast.success("Plan deleted successfully");
      setDeleteModal(false);
      navigate("/plan-subscription");
    } catch (err) {
      toast.error("Failed to delete plan");
      setDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };
  console.log("details", details);
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="View Subscription" pageTitle="Paal Dappa" />

        <Row>
          <Col lg={12}>


            <Card className="shadow-lg border-0 rounded-3 p-4 position-relative">
              <h3 className="text-primary fw-bold">{details.name}</h3>
              {/* Edit & Delete Icons */}
              <div className="position-absolute top-0 end-0 mt-3 me-3">
                <div className="d-flex gap-2">
                  {/* Edit Button */}
                  <div>
                    <Tooltip placement="top" target="EditTooltip">
                      Edit
                    </Tooltip>
                    <a href={`/plan-subscription/edit/${id}`} id="EditTooltip" className="btn btn-light">
                      <i className="ri-pencil-fill align-bottom"></i>
                    </a>
                  </div>

                  {/* Delete Button */}
                  <div>
                    <Tooltip placement="top" target="DeleteTooltip">
                      Delete
                    </Tooltip>
                    <span
                      id="DeleteTooltip"
                      className="btn btn-light"
                      onClick={() => setDeleteModal(true)}
                    >
                      <i className="mdi mdi-trash-can-outline align-bottom"></i>
                    </span>
                  </div>
                </div>
              </div>
              <CardBody>
                {/* Tabs */}
                <Nav tabs className="nav-tabs-custom">
                  <NavItem>
                    <NavLink
                      href="#"
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => toggleTab("1")}
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={classnames({ active: activeTab === "2" })}
                      onClick={() => toggleTab("2")}
                    >
                      User List
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="mt-4">
                  {/* Overview Tab */}
                  <TabPane tabId="1">
                    <Row className="gx-lg-5">
                      <Col xl={6}>
                        {details?.image && (
                          <div>
                            <img
                              src={details.image}
                              alt={details.name}
                              width={150}
                              height={100}
                              className="rounded mt-2"
                              style={{ cursor: "zoom-in" }}
                            />
                          </div>
                        )}
                      </Col>
                      <Col xl={6}>
                        <Card className="border-1 shadow-sm rounded p-3 bg-light mt-3">


                          {/* Table with Visible Borders */}
                          <h5 className="text-center fw-bold text-primary" > Total Amount details </h5>
                          <Table responsive bordered className="mb-0">
                            <thead className="table-light">
                              <tr>
                                <th className="text-start">Actual Amount</th>
                                {Number(details.total_offer_amount) !== 0 && (
                                  <th className="text-center">Offer Amount</th>
                                )}
                                <th className="text-end">Gross Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                {/* Actual Amount (strikethrough) */}
                                <td className="text-danger" style={{ textDecoration: "line-through" }}>
                                  ₹{Number(details.total_actual_amount).toFixed(2)}
                                </td>

                                {/* Conditional Offer Amount */}
                                {Number(details.total_offer_amount) !== 0 && (
                                  <td className="text-center">
                                    <span className="mr-1">↓</span>
                                    <span
                                      className={
                                        Number(details.total_actual_amount) > Number(details.total_gross_amount)
                                          ? "text-danger"
                                          : "text-success"
                                      }
                                    >
                                      {Number(details.total_actual_amount) > Number(details.total_gross_amount)
                                        ? "- "
                                        : "+ "}
                                      ₹{Math.abs(Number(details.total_offer_amount)).toFixed(2)}
                                    </span>
                                  </td>
                                )}

                                {/* Gross Amount */}
                                <td className="fw-bold text-success text-end">
                                  ₹{Number(details.total_gross_amount).toFixed(2)}
                                </td>
                              </tr>
                            </tbody>
                          </Table>

                        </Card>
                      </Col>



                    </Row>
                    <Row className="mt-4">
                      {/* Features & Description - Equal Size */}


                      <Col xl={9}>
                        <div className="border-0 shadow-sm rounded p-3 bg-light h-100">
                          <h5 className="text-secondary">Description</h5>
                          <p className="text-muted mb-0">
                            {showMore
                              ? <span dangerouslySetInnerHTML={{ __html: details.description || '' }} />
                              : <span dangerouslySetInnerHTML={{ __html: (details.description || '').slice(0, 250) + '...' }} />
                            }
                          </p>
                          {details.description?.length > 250 && (
                            <button className="btn btn-link p-0 text-primary" onClick={toggleReadMore}>
                              {showMore ? "Read Less" : "Read More"}
                            </button>
                          )}
                        </div>
                      </Col>

                      <Col xl={3}>
                        <div className="d-flex flex-column h-100 gap-3">
                          <Card className="border-0 shadow-sm rounded p-3 bg-light mb-0">
                            <h5 className="text-secondary">Cancel limit</h5>
                            <p className="fw-bold mb-0"> {details?.max_cancel_validity_days} Days</p>
                          </Card>
                          <Card className="border-0 shadow-sm rounded p-3 bg-light mb-0">
                            <h5 className="text-secondary">Subscription</h5>
                            <p className="fw-bold mb-0"> {details?.is_subscription ? "Active" : "Inactive"}</p>
                          </Card>
                          <Card className="border-0 shadow-sm rounded p-3 bg-light mb-0">
                            <h5 className="text-secondary">List Order</h5>
                            <p className="fw-bold mb-0"> {details?.list_order || 0}</p>
                          </Card>
                          {details?.type === 3 && (
                            <Row className="mt-2">
                              <Col md={6}>
                                <Card className="border-0 shadow-sm rounded p-2 bg-light mb-0 h-100">
                                  <h6 className="text-secondary small mb-1">Max Order Limit</h6>
                                  <p className="fw-bold mb-0"> {details?.max_purchase_count} Times</p>
                                  <small className="text-muted" style={{ fontSize: '10px' }}>Max times plan can be bought</small>
                                </Card>
                              </Col>
                              <Col md={6}>
                                <Card className="border-0 shadow-sm rounded p-2 bg-light mb-0 h-100">
                                  <h6 className="text-secondary small mb-1">Max Selectable Products</h6>
                                  <p className="fw-bold mb-0"> {details?.max_purchase_product} Products</p>
                                  <small className="text-muted" style={{ fontSize: '10px' }}>Distinct products user can pick</small>
                                </Card>
                              </Col>
                            </Row>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* Variants Table */}
                    {/* <Row className="mt-4">
                      <Col xl={12}>
                        <h5 className="text-secondary">Variants:</h5>
                        <div className="table-responsive">
                          <Table striped bordered className="bg-white rounded">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Product</th>
                                <th>Variant</th>
                                <th>Price details</th>
                                <th>Validity</th>
                                <th>Bonus</th>
                              </tr>
                            </thead>

                            <tbody>
                              {details?.details?.length > 0 ? (
                                details.details.map((detail: any, index: number) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{detail.product?.name || "N/A"}</td>
                                    <td>{detail.variant_value} {detail.variant?.name || ""}</td>
                                    <td>₹{detail.gross_amount}</td>
                                    <td>{detail.validity} {detail.validity_type}</td>
                                    <td>
                                      {detail?.is_loyalty_bonus === true ? (
                                        <span className="badge bg-success">Yes</span>
                                      ) : detail?.is_loyalty_bonus === false ? (
                                        <span className="badge bg-danger">No</span>
                                      ) : (
                                        <span className="badge bg-secondary">-</span>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={6} className="text-center">
                                    No variants available.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </Col>
                    </Row> */}

                    <Row className="mt-4">
                      <Col xl={12}>
                        <h5 className="text-secondary">Variants:</h5>
                        <div className="table-responsive">
                          <Table striped bordered className="bg-white rounded">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Product</th>
                                <th>Category</th>
                                <th>Variant</th>
                                <th>Price Details</th>
                                <th>Validity</th>
                                <th>Bonus</th>
                                <th>Offer</th>
                              </tr>
                            </thead>
                            <tbody>
                              {details?.details?.length > 0 ? (
                                details.details.map((detail: any, index: number) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        {detail.product?.icon && (
                                          <img
                                            src={detail.product.icon}
                                            alt={detail.product.name}
                                            className="me-2"
                                            style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                                          />
                                        )}
                                        <span>{detail.product?.name || "N/A"}</span>
                                      </div>
                                    </td>
                                    <td>
                                      {detail.category?.name || "N/A"}
                                    </td>
                                    <td>
                                      {detail.variant_value} {detail.variant?.name || ""}
                                    </td>
                                    <td>
                                      <div className="d-flex flex-column">
                                        <div className="d-flex align-items-baseline">
                                          <strong className="me-2">₹{detail.gross_amount}</strong>
                                          {detail.offer_type && (
                                            <span className="text-decoration-line-through text-muted small">
                                              ₹{detail.actual_amount}
                                            </span>
                                          )}
                                        </div>
                                        {detail?.per_day_amount && (
                                          <small className="text-muted">Per day: ₹{detail.per_day_amount}</small>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="d-flex flex-column">
                                        <span>
                                          {detail.validity} Days
                                        </span>
                                        {detail.free_validity_days > 0 && (
                                          <small className="text-success">
                                            +{detail.free_validity_days} free days
                                          </small>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      {detail?.is_loyalty_bonus ? (
                                        <span className="badge bg-success">Yes</span>
                                      ) : (
                                        <span className="badge bg-danger">No</span>
                                      )}
                                    </td>
                                    <td>
                                      {detail.offer_type === 1 ? (
                                        <div className="text-success small">
                                          <div>Special Offer</div>
                                          <div>₹{detail.offer_value} value</div>
                                        </div>
                                      ) : detail.offer_type === 2 ? (
                                        <div className="text-primary small">
                                          <div>{detail.offer_value}% Discount</div>
                                          <div>Save ₹{detail.actual_amount - detail.gross_amount}</div>
                                        </div>
                                      ) : (
                                        <span className="badge bg-secondary small">No offer</span>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={7} className="text-center">
                                    No variants available.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </Col>
                    </Row>

                    {/* Gallery Section */}
                    {details?.gallery?.length > 0 && (
                      <Row className="mt-5">
                        <Col lg={12}>
                          <h5 className="text-secondary mb-3">Subscription Gallery</h5>
                          <Row>
                            {details.gallery
                              .sort((a: any, b: any) => (a.listOrder || 0) - (b.listOrder || 0))
                              .map((item: any, index: number) => (
                                <Col xl={2} md={3} sm={4} key={item.id} className="mb-4">
                                  <Card className="h-100 border shadow-none mb-0">
                                    <img
                                      src={item.image}
                                      alt={`Gallery ${index}`}
                                      className="card-img-top img-fluid rounded"
                                      style={{ height: '140px', objectFit: 'cover', cursor: 'zoom-in' }}
                                      onClick={() => window.open(item.image, '_blank')}
                                    />
                                    {item.listOrder > 0 && (
                                      <div className="card-footer p-1 text-center bg-light">
                                        <small className="text-muted">Order: {item.listOrder}</small>
                                      </div>
                                    )}
                                  </Card>
                                </Col>
                              ))}
                          </Row>
                        </Col>
                      </Row>
                    )}
                  </TabPane>

                  {/* User List Tab */}
                  <TabPane tabId="2">

                    <UserPlanSubscriptionList />
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        content={
          <>
            Do you want to delete <strong>{details.name}</strong>?
          </>
        }
      />
    </div >
  );
};

export default ViewPlanSubscription;
